import { formatDuration } from "../executionTimeline";

// Los contadores del backend miden cosas distintas con nombres parecidos:
// `steps_in_graph` son los pasos que el workflow tiene, `step_runs` las veces
// que se corrió alguno, y `distinct_steps_run` cuántos de los del grafo llegó a
// tocar. Se muestran juntos para que no se lean como el mismo número.

const Metric = ({ label, value, hint, tone = "text-white" }) => (
  <div className="min-w-0">
    <div className="text-[10px] font-medium uppercase tracking-wider text-gray-500">{label}</div>
    <div className={`mt-1 text-xl font-semibold leading-none ${tone}`}>{value}</div>
    {hint && <div className="mt-1 text-[10px] text-gray-500">{hint}</div>}
  </div>
);

/**
 * La proporción entre trabajo y espera, que es el dato que no se ve por
 * ningún lado hoy. Se calcula sobre lo medido, no sobre el total: una
 * ejecución en curso todavía no tiene total.
 */
const WorkSplit = ({ runningMs, waitingMs }) => {
  const measured = (runningMs || 0) + (waitingMs || 0);
  if (!measured) return null;

  const runningPct = (runningMs / measured) * 100;
  const waitingPct = 100 - runningPct;
  const readable = (pct) => (pct >= 1 ? `${Math.round(pct)}%` : "<1%");

  return (
    <div className="mt-4">
      <div className="flex h-2 overflow-hidden rounded-full bg-zinc-800">
        <div className="bg-emerald-500" style={{ width: `${runningPct}%` }} title="Working" />
        <div className="bg-blue-500" style={{ width: `${waitingPct}%` }} title="Waiting" />
      </div>
      <div className="mt-2 text-[11px] text-gray-500">
        Waiting accounts for {readable(waitingPct)} of this execution.
      </div>
    </div>
  );
};

export default function ExecutionMetrics({ counters, duration, coins }) {
  const runningMs = duration?.running_ms ?? 0;
  const waitingMs = duration?.waiting_ms ?? 0;
  // `total_ms` es null mientras la ejecución no cierre. Lo medido sí existe,
  // así que se muestra eso en vez de un total inventado.
  const totalMs = duration?.total_ms ?? null;

  return (
    <div className="rounded-xl border border-white/10 bg-zinc-900/50 px-5 py-4">
      <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4 lg:grid-cols-8">
        <Metric
          label="Total time"
          value={totalMs === null ? "In progress" : formatDuration(totalMs)}
          hint={totalMs === null ? "not finished yet" : null}
        />
        <Metric label="Working" value={formatDuration(runningMs)} tone="text-emerald-400" />
        <Metric label="Waiting" value={formatDuration(waitingMs)} tone="text-blue-400" />
        <Metric label="Step runs" value={counters?.step_runs ?? "—"} />
        <Metric
          label="Steps reached"
          value={`${counters?.distinct_steps_run ?? "—"} / ${counters?.steps_in_graph ?? "—"}`}
          hint={counters ? `${counters.not_executed} never ran` : null}
        />
        <Metric label="Waits" value={counters?.waits ?? "—"} />
        <Metric label="Loop passes" value={counters?.loop_passes ?? "—"} />
        <Metric
          label="Failed"
          value={counters?.failed ?? "—"}
          tone={counters?.failed ? "text-rose-400" : "text-white"}
        />
      </div>

      <WorkSplit runningMs={runningMs} waitingMs={waitingMs} />

      {coins !== null && coins !== undefined && (
        <div className="mt-3 flex items-center gap-1.5 text-[11px] text-amber-300/90">
          <span className="inline-block h-2 w-2 rounded-full bg-amber-400" />
          {coins} {coins === 1 ? "coin" : "coins"} charged across this execution
        </div>
      )}
    </div>
  );
}
